import {
  MessageBar,
  MessageBarBody,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { PopupDialog, tokens } from '@vigeo/ui';
import { sentCopy } from './model.js';

/**
 * 2.4 — the sent window (Paul, 2026-09-29 15:45; #373).
 *
 * **Its link is the designed way into Row 3 from here** — and only from here. An earlier version of
 * this note claimed it was the ONLY route and that without it a user would have to type a URL. That
 * was wrong, and a review of #396 caught it: `PersonPage` puts "Documents & checklist" in the rail
 * unconditionally (`pick('documents')`, with no `available: false`), so 3.1 is always one click
 * away. What this offers is the route AT THE MOMENT it is wanted, off the screen that just sent the
 * packet, which is why it is a window and not a toast — but #373 does not sit in front of #357
 * because 3.1 would otherwise be unreachable.
 *
 * TRANSIENT BY DESIGN, and that is a decision rather than laziness. The persistent record of the
 * send is 2.5, the "Hired on" summary the section shows from then on. Storing "was this window
 * dismissed" would put a fact about one browser tab into the person's record, and a second HR user
 * opening the same candidate would either see a confirmation of something they did not do or be
 * denied the link. So it shows once, to whoever sent, and 2.5 carries the durable version.
 */

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
});

export interface SentOnboardingItemsProps {
  readonly open: boolean;
  readonly firstName: string;
  /**
   * What could not be done, when the forms went out but something adjacent did not — a missing
   * Welcome template, a send that failed. Reported HERE rather than swallowed: the move succeeded
   * and the email did not, and those are two facts. Null when everything worked.
   */
  readonly warning: string | null;
  /** Undefined where the caller has nowhere to send them; the window then shows Close alone. */
  readonly onGoToDocuments?: () => void;
  readonly onClose: () => void;
}

export function SentOnboardingItems({ open, firstName, warning, onGoToDocuments, onClose }: SentOnboardingItemsProps) {
  const styles = useStyles();

  // A result, not a question (PG13): no Cancel. With a next step it is "Close" on the left and one blue button naming the step on
  // the right; with none it is a lone blue "Close". The keyboard starts on the blue button; the X and Esc close it.
  // No `opener`: it opens by itself after a send, and the button that led here is replaced by the onboarding view, so there is nothing to return to.
  const hasNextStep = onGoToDocuments !== undefined;
  return (
    <PopupDialog
      open={open}
      title={sentCopy.title}
      startOn="primary"
      showX={false}
      onClose={onClose}
      cancelLabel={hasNextStep ? sentCopy.close : null}
      primary={hasNextStep ? { label: sentCopy.goToDocuments, onClick: onGoToDocuments } : { label: sentCopy.close, onClick: onClose }}
    >
      <div className={styles.body}>
        <Text>{sentCopy.sent(firstName)}</Text>
        {/*
          The forms DID go out. A warning here is about something alongside them, so it is
          `warning` and never `error` — an intent that says "this failed" over a screen that says
          "this worked" is how somebody sends a packet twice.
        */}
        {warning === null ? null : (
          /*
            THE LIVE REGION IS OURS, not Fluent's. `OfferStageSection`'s own `notice` carries
            `role="status"` for exactly this reason — Fluent's `MessageBar` announce is inert
            without a provider, so without this a screen-reader user taking the primary route is
            never told the welcome email failed. It is also the only part of the warning/error
            decision a jsdom test can assert, which is why the test reads this role.
          */
          <div role="status">
            <MessageBar intent="warning" politeness="polite">
              <MessageBarBody>{warning}</MessageBarBody>
            </MessageBar>
          </div>
        )}
      </div>
    </PopupDialog>
  );
}
